// WebAuthn helper utilities

const base64UrlEncode = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const base64UrlDecode = (str) => {
  let output = str.replace(/-/g, '+').replace(/_/g, '/');
  switch (output.length % 4) {
    case 0:
      break;
    case 2:
      output += '==';
      break;
    case 3:
      output += '=';
      break;
    default:
      throw new Error('Invalid base64url string');
  }
  const binaryString = atob(output);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64ToBuffer = (base64) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

// Convert server options for browser API
const convertRegistrationOptions = (options) => {
  return {
    ...options,
    challenge: base64UrlDecode(options.challenge),
    user: {
      ...options.user,
      id: base64UrlDecode(options.user.id)
    }
  };
};

const convertAuthenticationOptions = (options) => {
  return {
    ...options,
    challenge: base64UrlDecode(options.challenge),
    allowCredentials: options.allowCredentials?.map(cred => ({
      ...cred,
      id: base64UrlDecode(cred.id)
    }))
  };
};

// Register passkey
export const registerPasskey = async (registrationOptions, nickname) => {
  if (!window.PublicKeyCredential) {
    throw new Error('WebAuthn not supported on this device');
  }

  try {
    const options = convertRegistrationOptions(registrationOptions);
    const credential = await navigator.credentials.create({
      publicKey: options
    });

    if (!credential) {
      throw new Error('Passkey creation cancelled or failed');
    }

    return {
      id: base64UrlEncode(credential.id),
      response: {
        publicKey: bufferToBase64(credential.response.publicKey),
        attestationObject: bufferToBase64(credential.response.attestationObject),
        clientDataJSON: bufferToBase64(credential.response.clientDataJSON),
        transports: credential.response.transports || []
      },
      nickname
    };
  } catch (err) {
    console.error('Passkey registration error:', err);
    throw err;
  }
};

// Authenticate with passkey
export const authenticatePasskey = async (authenticationOptions) => {
  if (!window.PublicKeyCredential) {
    throw new Error('WebAuthn not supported on this device');
  }

  try {
    const options = convertAuthenticationOptions(authenticationOptions);
    const credential = await navigator.credentials.get({
      publicKey: options
    });

    if (!credential) {
      throw new Error('Passkey authentication cancelled or failed');
    }

    return {
      id: base64UrlEncode(credential.id),
      response: {
        authenticatorData: bufferToBase64(credential.response.authenticatorData),
        clientDataJSON: bufferToBase64(credential.response.clientDataJSON),
        signature: bufferToBase64(credential.response.signature),
        userHandle: credential.response.userHandle ? bufferToBase64(credential.response.userHandle) : null
      }
    };
  } catch (err) {
    console.error('Passkey authentication error:', err);
    throw err;
  }
};

// Check if WebAuthn is available
export const isWebAuthnAvailable = () => {
  return !!window.PublicKeyCredential;
};

// Check if platform authenticator is available (biometric/PIN)
export const isPlatformAuthenticatorAvailable = async () => {
  if (!isWebAuthnAvailable()) return false;
  return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.();
};

export { bufferToBase64, base64ToBuffer, base64UrlEncode, base64UrlDecode };
